/**
 * InteractiveSiteTour - Custom High-Performance React Onboarding Walkthrough
 * Highlights DOM elements in sequence (Home, Browse, Chat, My Prompts, Saved, Create Prompt)
 * using a semi-transparent dimmed overlay, dynamic spotlight, and a themed popover.
 * Features:
 *   - Compact card width on mobile (170px) positioned on the right next to elements
 *   - Removed auto-opening menu drawer code
 *   - Fully themed to primary blue
 *   - Responds to window resizing for flawless positioning
 */

import { useEffect, useState, useLayoutEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

interface TourStep {
  selector: string;
  mobileSelector: string;
  title: string;
  description: string;
  icon: string;
  position: 'bottom' | 'top' | 'left' | 'right';
  targetPath?: string; // Optional path navigation before step
}

const TOUR_STEPS: TourStep[] = [
  {
    selector: '#nav-home',
    mobileSelector: '#mobile-nav-home',
    title: 'Trending Prompts Feed',
    description: 'Explore the hottest, most engaging prompts voted by the community.',
    icon: '🔥',
    position: 'bottom',
    targetPath: '/',
  },
  {
    selector: '#nav-browse',
    mobileSelector: '#mobile-nav-browse',
    title: 'Search & Filter',
    description: 'Browse the entire prompts library. Filter by categories.',
    icon: '🔍',
    position: 'bottom',
  },
  {
    selector: '#nav-chat',
    mobileSelector: '#mobile-nav-chat',
    title: 'Community Chat',
    description: 'Collaborate, talk shop, and share prompt ideas.',
    icon: '💬',
    position: 'bottom',
  },
  {
    selector: '#nav-my-prompts',
    mobileSelector: '#mobile-nav-my-prompts',
    title: 'Your Dashboard',
    description: 'Track the performance, views, and likes of your prompts.',
    icon: '📋',
    position: 'bottom',
  },
  {
    selector: '#nav-saved',
    mobileSelector: '#mobile-nav-saved',
    title: 'Saved Library',
    description: 'Keep your favorite prompts bookmarked in one place.',
    icon: '🔖',
    position: 'bottom',
  },
  {
    selector: '#nav-create-prompt',
    mobileSelector: '#mobile-nav-create-prompt',
    title: 'Create & Share',
    description: 'Share your creative prompts with the world.',
    icon: '✏️',
    position: 'bottom',
  },
];

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export default function InteractiveSiteTour({ isOpen, onClose }: Props) {
  const { state: { isAuthenticated } } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [showLoginNudge, setShowLoginNudge] = useState(false);
  const [popoverCoords, setPopoverCoords] = useState({ top: 0, left: 0 });
  const [spotlightCoords, setSpotlightCoords] = useState({ top: 0, left: 0, width: 0, height: 0 });
  const [activeElementFound, setActiveElementFound] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const step = TOUR_STEPS[currentStep];

  // Recalculate coordinates based on target DOM element or mobile size
  const updateCoords = () => {
    if (!isOpen || !step) return;

    const width = window.innerWidth;
    const isMobile = width < 768;

    const targetSelector = isMobile ? step.mobileSelector : step.selector;
    const element = document.querySelector(targetSelector);

    if (!element) {
      setActiveElementFound(false);
      return;
    }

    const rect = element.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      setActiveElementFound(false);
      return;
    }

    setActiveElementFound(true);

    // Spotlight coordinates relative to viewport
    const padding = 6;
    setSpotlightCoords({
      top: rect.top - padding,
      left: rect.left - padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
    });

    // Popover alignment calculation
    let popTop = 0;
    let popLeft = 0;

    if (isMobile) {
      const cardWidth = 170;
      const approxHeight = 120;

      // Position the card on the right side of the screen next to the left-aligned mobile menu links
      popLeft = window.innerWidth - cardWidth - 8;

      // Align card vertically centered with the targeted link element
      popTop = rect.top + rect.height / 2 - approxHeight / 2;

      // Vertical safety clamp bounds
      if (popTop < 8) popTop = 8;
      if (popTop + approxHeight > window.innerHeight - 8) {
        popTop = window.innerHeight - approxHeight - 8;
      }
    } else {
      if (step.position === 'bottom') {
        popTop = rect.bottom + 12;
        popLeft = rect.left + rect.width / 2 - 160;
      } else if (step.position === 'top') {
        popTop = rect.top - 180;
        popLeft = rect.left + rect.width / 2 - 160;
      }

      // Viewport bounds constraint to avoid cropping on desktop
      const viewportWidth = window.innerWidth;
      if (popLeft < 16) popLeft = 16;
      if (popLeft + 320 > viewportWidth - 16) popLeft = viewportWidth - 320 - 16;

      // Vertically clamp within page height bounds
      if (popTop < 6) popTop = 6;
    }

    setPopoverCoords({ top: popTop, left: popLeft });
  };

  // Trigger coordinate recalculation on step change, resize, or route navigation
  useLayoutEffect(() => {
    updateCoords();
  }, [currentStep, isOpen, location.pathname]);

  // Open mobile menu if needed when tour opens in mobile view
  useEffect(() => {
    if (!isOpen) return;

    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      const hasMobileNav = document.querySelector('#mobile-nav-home');
      if (!hasMobileNav) {
        const toggleBtn = document.querySelector('#mobile-menu-toggle-btn') as HTMLButtonElement | null;
        if (toggleBtn) {
          toggleBtn.click();
          
          // Wait for menu animation/mount before calculating coords
          const timer = setTimeout(() => {
            updateCoords();
          }, 150);
          return () => clearTimeout(timer);
        }
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener('resize', updateCoords);
    window.addEventListener('scroll', updateCoords);
    return () => {
      window.removeEventListener('resize', updateCoords);
      window.removeEventListener('scroll', updateCoords);
    };
  }, [isOpen, currentStep]);

  // Navigate to path if defined on step change
  const handleNext = () => {
    const nextIndex = currentStep + 1;
    if (nextIndex >= TOUR_STEPS.length) {
      handleFinish();
      return;
    }

    const nextStep = TOUR_STEPS[nextIndex];
    if (nextStep.targetPath && location.pathname !== nextStep.targetPath) {
      navigate(nextStep.targetPath);
    }
    setCurrentStep(nextIndex);
  };

  const handleBack = () => {
    const prevIndex = currentStep - 1;
    if (prevIndex < 0) return;

    const prevStep = TOUR_STEPS[prevIndex];
    if (prevStep.targetPath && location.pathname !== prevStep.targetPath) {
      navigate(prevStep.targetPath);
    }
    setCurrentStep(prevIndex);
  };

  const closeMobileMenuIfNeeded = () => {
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      const hasMobileNav = document.querySelector('#mobile-nav-home');
      if (hasMobileNav) {
        const toggleBtn = document.querySelector('#mobile-menu-toggle-btn') as HTMLButtonElement | null;
        if (toggleBtn) {
          toggleBtn.click();
        }
      }
    }
  };

  const handleCloseAll = () => {
    closeMobileMenuIfNeeded();
    setCurrentStep(0);
    setShowLoginNudge(false);
    onClose();
  };

  const handleFinish = () => {
    closeMobileMenuIfNeeded();
    if (!isAuthenticated) {
      setShowLoginNudge(true);
    } else {
      handleCloseAll();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 pointer-events-none">
      {/* Dimmed overlay backdrops */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-[1px] pointer-events-auto transition-opacity duration-300"
        onClick={handleCloseAll}
        aria-hidden="true"
      />

      {showLoginNudge ? (
        /* Gorgeous, compact custom Centered Login Nudge Popover at Tour End */
        <div
          className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-white rounded-xl shadow-2xl p-4 w-[280px] md:w-[320px] pointer-events-auto border border-primary-100 flex flex-col gap-3 text-center"
          role="dialog"
          aria-modal="true"
          aria-label="Unlock the full experience"
        >
          <div>
            <div className="text-3xl mb-1.5" aria-hidden="true">🔓</div>
            <h3 className="text-sm md:text-base font-bold text-gray-900 mb-1">
              Unlock the Full Experience!
            </h3>
            <p className="text-[11px] md:text-xs text-gray-600 leading-relaxed">
              Sign in now to save favorite prompts, join the community chat, request custom AI photos, and share your own prompts with the world!
            </p>
          </div>

          <div className="flex flex-col gap-2 pt-1">
            <button
              onClick={() => {
                navigate('/login');
                handleCloseAll();
              }}
              className="w-full text-xs font-bold text-white bg-primary-600 hover:bg-primary-700 py-2 rounded-lg transition-colors shadow-sm"
            >
              Sign In
            </button>
            <button
              onClick={() => {
                navigate('/register');
                handleCloseAll();
              }}
              className="w-full text-xs font-bold text-primary-600 hover:bg-primary-50 border border-primary-500 py-2 rounded-lg transition-colors"
            >
              Create Free Account →
            </button>
            <button
              onClick={handleCloseAll}
              className="text-[10px] text-gray-400 hover:text-gray-600 transition-colors py-0.5"
            >
              Maybe later
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Spotlit focused element border */}
          {activeElementFound && (
            <div
              className="fixed z-50 rounded-lg border-2 border-primary-500 shadow-[0_0_0_9999px_rgba(0,0,0,0.6),0_0_15px_rgba(59,130,246,0.5)] transition-all duration-300 pointer-events-none"
              style={{
                top: `${spotlightCoords.top}px`,
                left: `${spotlightCoords.left}px`,
                width: `${spotlightCoords.width}px`,
                height: `${spotlightCoords.height}px`,
              }}
            />
          )}

          {/* Interactive Tooltip Popover */}
          <div
            className={`fixed z-50 bg-white rounded-xl shadow-2xl p-2.5 md:p-4 w-[170px] md:w-[320px] pointer-events-auto border border-primary-100 flex flex-col gap-2 transition-all duration-300 ${
              activeElementFound
                ? ''
                : 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 shadow-primary-500/10'
            }`}
            style={
              activeElementFound
                ? {
                    top: `${popoverCoords.top}px`,
                    left: `${popoverCoords.left}px`,
                  }
                : undefined
            }
            role="dialog"
            aria-modal="true"
            aria-label={`Tour step: ${step?.title}`}
          >
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <span className="text-sm md:text-xl" aria-hidden="true">{step?.icon}</span>
                <span className="text-[9px] md:text-xs font-semibold text-primary-600 bg-primary-50 px-1.5 py-0.5 rounded-full">
                  {currentStep + 1}/{TOUR_STEPS.length}
                </span>
              </div>
              <button
                onClick={handleFinish}
                className="text-gray-400 hover:text-gray-600 transition-colors p-0.5"
                aria-label="Skip tour"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div>
              <h3 className="text-[11px] md:text-sm font-bold text-gray-900 mb-0.5 leading-snug">{step?.title}</h3>
              <p className="text-[10px] md:text-xs text-gray-600 leading-tight">{step?.description}</p>
            </div>

            {/* Buttons / Walkthrough Controls */}
            <div className="flex items-center justify-between border-t border-gray-100 pt-2">
              <button
                onClick={handleFinish}
                className="text-[9px] md:text-[11px] text-gray-400 hover:text-gray-600 font-medium transition-colors"
              >
                Skip
              </button>

              <div className="flex items-center gap-1">
                <button
                  onClick={handleBack}
                  disabled={currentStep === 0}
                  className="text-[9px] md:text-xs font-semibold text-gray-600 hover:text-primary-600 bg-gray-50 border border-gray-200 px-1.5 py-1 rounded-md transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center"
                >
                  ←
                </button>
                <button
                  onClick={handleNext}
                  className="text-[9px] md:text-xs font-bold text-white bg-primary-600 hover:bg-primary-700 px-2 py-1 rounded-md transition-colors flex items-center"
                >
                  {currentStep === TOUR_STEPS.length - 1 ? 'End' : 'Next'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
